import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGitBranches, useGitCommits } from "@/lib/queries";
import { timeAgo } from "@/lib/utils";
import { Input, Select } from "./ui";

const MANUAL = "__manual__";

/**
 * Picks a commit: branch (defaults to the app branch / repository default) → one of its recent
 * commits (latest selected by default), or a SHA typed manually. Falls back to a plain input when
 * the app has no git provider.
 */
export function CommitPicker({
  appId,
  hasGit,
  value,
  onChange,
  autoSelectLatest = true,
  idPrefix = "commit",
}: {
  appId: string;
  hasGit: boolean;
  value: string;
  onChange: (sha: string) => void;
  autoSelectLatest?: boolean;
  idPrefix?: string;
}) {
  const { t, i18n } = useTranslation();
  const branches = useGitBranches(appId, hasGit);
  const [branch, setBranch] = useState("");
  const [manual, setManual] = useState(false);
  useEffect(() => {
    if (!branch && branches.data) setBranch(branches.data.defaultBranch ?? branches.data.branches[0]?.name ?? "");
  }, [branch, branches.data]);
  const commits = useGitCommits(appId, branch, hasGit && !manual);
  useEffect(() => {
    if (autoSelectLatest && !manual && commits.data?.[0] && !commits.data.some((c) => c.sha === value)) onChange(commits.data[0].sha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commits.data]);

  if (!hasGit || branches.error || manual) {
    return (
      <div className="space-y-1">
        <Input id={`${idPrefix}-sha`} value={value} onChange={(e) => onChange(e.target.value.trim())} placeholder="a1b2c3d… / main" className="font-mono" />
        {hasGit && branches.error && <p className="text-xs text-red-600">{(branches.error as Error).message}</p>}
        {hasGit && manual && !branches.error && (
          <button type="button" className="text-xs text-brand-600 hover:underline" onClick={() => setManual(false)}>
            {t("commitPicker.pickFromList")}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="grid gap-2 sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)]">
      <Select id={`${idPrefix}-branch`} aria-label={t("commitPicker.branch")} value={branch} onChange={(e) => setBranch(e.target.value)} disabled={branches.isLoading}>
        {branches.data?.branches.map((b) => (
          <option key={b.name} value={b.name}>
            {b.name}
          </option>
        ))}
      </Select>
      <Select
        id={`${idPrefix}-sha`}
        aria-label={t("commitPicker.commit")}
        value={commits.data?.some((c) => c.sha === value) ? value : ""}
        onChange={(e) => (e.target.value === MANUAL ? setManual(true) : onChange(e.target.value))}
        disabled={commits.isLoading}
        className="font-mono text-xs"
      >
        {!autoSelectLatest && <option value="">{t("commitPicker.none")}</option>}
        {commits.data?.map((c) => (
          <option key={c.sha} value={c.sha}>
            {c.sha.slice(0, 8)} · {c.message.slice(0, 60)}
            {c.date ? ` · ${timeAgo(c.date, i18n.language)}` : ""}
          </option>
        ))}
        <option value={MANUAL}>{t("commitPicker.manual")}</option>
      </Select>
    </div>
  );
}
